import React from "react";
import {
  FaUserShield,
  FaDatabase,
  FaCookieBite,
  FaLock,
  FaShareAlt,
  FaGlobe,
  FaShoppingBag,
  FaEnvelope,
} from "react-icons/fa";

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: <FaUserShield />,
      title: "Information We Collect",
      content:
        "When you use Glamira, we may collect personal information such as your name, email address, phone number, shipping address, billing details, and account credentials. We also collect information related to your orders, browsing activity, and interactions with our website to improve your shopping experience.",
    },
    {
      icon: <FaShoppingBag />,
      title: "How We Use Your Information",
      list: [
        "Process and fulfill your orders.",
        "Provide customer support and respond to inquiries.",
        "Send order confirmations, shipping updates, and important notifications.",
        "Improve our website, products, and services.",
        "Detect and prevent fraud or unauthorized activities.",
        "Comply with applicable legal obligations.",
      ],
    },
    {
      icon: <FaCookieBite />,
      title: "Cookies & Tracking Technologies",
      content:
        "We use cookies and similar technologies to remember your preferences, enhance website performance, analyze visitor behavior, and provide a personalized shopping experience. You may disable cookies through your browser settings, although certain features may not function properly.",
    },
    {
      icon: <FaShareAlt />,
      title: "Sharing of Information",
      content:
        "We do not sell or rent your personal information to third parties. Your information may only be shared with trusted service providers such as payment gateways, logistics partners, and technology providers strictly for processing orders, deliveries, and providing website functionality.",
    },
    {
      icon: <FaLock />,
      title: "Data Security",
      content:
        "We implement industry-standard security measures, including encryption, secure payment processing, and restricted access controls to protect your personal information against unauthorized access, misuse, or disclosure.",
    },
    {
      icon: <FaDatabase />,
      title: "Data Retention",
      content:
        "We retain your information only for as long as necessary to fulfill orders, comply with legal obligations, resolve disputes, and maintain business records. Once no longer required, your information is securely deleted or anonymized.",
    },
    {
      icon: <FaGlobe />,
      title: "Your Rights",
      list: [
        "Request access to your personal information.",
        "Request correction of inaccurate information.",
        "Request deletion of eligible personal data.",
        "Opt out of promotional communications at any time.",
        "Contact us regarding any privacy-related concerns.",
      ],
    },
    {
      icon: <FaEnvelope />,
      title: "Contact Us",
      content:
        "If you have any questions regarding this Privacy Policy or the way your personal information is handled, please contact our customer support team through the Contact Us page available on our website.",
    },
  ];

  return (
    <div
      style={{
        background: "#f5f7fb",
        minHeight: "100vh",
        padding: "60px 0",
      }}
    >
      <div className="container">

        {/* Hero Section */}
        <div
          className="text-center text-white rounded-4 shadow-lg p-5 mb-5"
          style={{
            background:
              "linear-gradient(135deg, #0f172a 0%, #2563eb 100%)",
          }}
        >
          <h1 className="display-4 fw-bold">Privacy Policy</h1>

          <p className="lead mt-3 mb-0">
            Your privacy matters to us. We are committed to protecting your
            personal information and maintaining your trust.
          </p>
        </div>

        {/* Introduction */}

        <div className="card border-0 shadow-sm rounded-4 mb-5">
          <div className="card-body p-5">
            <h3 className="fw-bold mb-3">
              Our Commitment to Privacy
            </h3>

            <p className="text-muted mb-0">
              At Glamira, protecting customer privacy is one of our highest
              priorities. This Privacy Policy explains how we collect, use,
              store, and safeguard your information whenever you browse our
              website, create an account, or place an order.
            </p>
          </div>
        </div>

        {/* Sections */}

        <div className="row">
          {sections.map((section, index) => (
            <div className="col-lg-6 mb-4" key={index}>
              <div className="card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body p-4">

                  <div className="d-flex align-items-center mb-3">

                    <div
                      className="rounded-circle text-white d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: 55,
                        height: 55,
                        background:
                          "linear-gradient(135deg,#2563eb,#1d4ed8)",
                        fontSize: 22,
                      }}
                    >
                      {section.icon}
                    </div>

                    <div>
                      <small className="text-primary fw-bold">
                        Section {index + 1}
                      </small>
                      <h5 className="fw-bold mb-0">
                        {section.title}
                      </h5>
                    </div>

                  </div>

                  {section.content && (
                    <p className="text-muted mb-0">
                      {section.content}
                    </p>
                  )}

                  {section.list && (
                    <ul className="text-muted ps-3 mb-0">
                      {section.list.map((item, i) => (
                        <li key={i} className="mb-2">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}

                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Important Notice */}

        <div
          className="mt-5 p-4 rounded-4 shadow-sm"
          style={{
            background: "#ecfeff",
            borderLeft: "6px solid #06b6d4",
          }}
        >
          <h4 className="fw-bold mb-3">
            🔒 Privacy Commitment
          </h4>

          <p className="mb-0 text-muted">
            Glamira does not sell your personal information to third parties.
            We only share information with trusted partners required for
            payment processing, shipping, and website operations to provide
            you with a seamless shopping experience.
          </p>
        </div>

        {/* CTA */}

        <div className="card border-0 shadow-lg rounded-4 mt-5">
          <div className="card-body text-center p-5">

            <h2 className="fw-bold mb-3">
              Questions About Your Privacy?
            </h2>

            <p
              className="text-muted mx-auto"
              style={{ maxWidth: "700px" }}
            >
              If you have any questions about how we collect, use, or protect
              your personal information, our support team is here to help.
            </p>

            <a
              href="/contact"
              className="btn btn-primary btn-lg px-5 mt-3"
            >
              Contact Us
            </a>

          </div>
        </div>

        {/* Footer */}

        <div className="text-center mt-5 pt-4 border-top">

          <h5 className="fw-bold">
            Your Trust is Important to Us
          </h5>

          <p className="text-muted mb-2">
            We continuously work to ensure your information remains secure and
            your shopping experience stays safe and transparent.
          </p>

          <small className="text-secondary">
            © {new Date().getFullYear()} Glamira. All Rights Reserved.
          </small>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;